import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import OtelMetricsPage from './OtelMetricsPage.jsx';
import BenchmarkPage from './BenchmarkPage.jsx';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import {
  Activity, Bot, User, GitBranch, MessageSquare, FileText,
  Calendar, TrendingUp, Clock, Zap, Shield, Link2,
  ChevronRight, Sparkles, CheckCircle, ArrowRight,
  Layers, Database, Lock, Globe, Users, Brain,
  GitPullRequest, GitMerge, Code2, Rocket, AlertCircle,
  FileCode, Terminal, GitFork, Flame, ExternalLink
} from 'lucide-react';

// Engineering metrics data
const engineeringRadarCurrent = [
  { dimension: 'Code Quality', value: 87, fullMark: 100 },
  { dimension: 'Velocity', value: 92, fullMark: 100 },
  { dimension: 'Collaboration', value: 78, fullMark: 100 },
  { dimension: 'Tech Leadership', value: 85, fullMark: 100 },
  { dimension: 'Project Impact', value: 94, fullMark: 100 },
  { dimension: 'Review Depth', value: 81, fullMark: 100 },
  { dimension: 'Knowledge Share', value: 73, fullMark: 100 },
  { dimension: 'Incident Response', value: 88, fullMark: 100 },
];

const engineeringRadarPrevious = [
  { dimension: 'Code Quality', value: 82, fullMark: 100 },
  { dimension: 'Velocity', value: 88, fullMark: 100 },
  { dimension: 'Collaboration', value: 71, fullMark: 100 },
  { dimension: 'Tech Leadership', value: 79, fullMark: 100 },
  { dimension: 'Project Impact', value: 90, fullMark: 100 },
  { dimension: 'Review Depth', value: 75, fullMark: 100 },
  { dimension: 'Knowledge Share', value: 68, fullMark: 100 },
  { dimension: 'Incident Response', value: 85, fullMark: 100 },
];

// Junior engineer radar data - same dimensions, lower scores
const juniorRadarCurrent = [
  { dimension: 'Code Quality', value: 68, fullMark: 100 },
  { dimension: 'Velocity', value: 74, fullMark: 100 },
  { dimension: 'Collaboration', value: 82, fullMark: 100 },
  { dimension: 'Tech Leadership', value: 52, fullMark: 100 },
  { dimension: 'Project Impact', value: 61, fullMark: 100 },
  { dimension: 'Review Depth', value: 59, fullMark: 100 },
  { dimension: 'Knowledge Share', value: 48, fullMark: 100 },
  { dimension: 'Incident Response', value: 55, fullMark: 100 },
];

const juniorRadarPrevious = [
  { dimension: 'Code Quality', value: 58, fullMark: 100 },
  { dimension: 'Velocity', value: 65, fullMark: 100 },
  { dimension: 'Collaboration', value: 75, fullMark: 100 },
  { dimension: 'Tech Leadership', value: 42, fullMark: 100 },
  { dimension: 'Project Impact', value: 48, fullMark: 100 },
  { dimension: 'Review Depth', value: 47, fullMark: 100 },
  { dimension: 'Knowledge Share', value: 38, fullMark: 100 },
  { dimension: 'Incident Response', value: 45, fullMark: 100 },
];

// AI Engineer radar data - depth in AI/ML, less breadth than Sr Engineer
const aiEngineerRadarCurrent = [
  { dimension: 'Model Architecture', value: 96, fullMark: 100 },
  { dimension: 'Training & Tuning', value: 93, fullMark: 100 },
  { dimension: 'Prompt Engineering', value: 89, fullMark: 100 },
  { dimension: 'Data Pipelines', value: 85, fullMark: 100 },
  { dimension: 'ML Infrastructure', value: 88, fullMark: 100 },
  { dimension: 'Research & Innovation', value: 91, fullMark: 100 },
  { dimension: 'Model Evaluation', value: 87, fullMark: 100 },
  { dimension: 'Code Quality', value: 72, fullMark: 100 },
];

const aiEngineerRadarPrevious = [
  { dimension: 'Model Architecture', value: 88, fullMark: 100 },
  { dimension: 'Training & Tuning', value: 85, fullMark: 100 },
  { dimension: 'Prompt Engineering', value: 82, fullMark: 100 },
  { dimension: 'Data Pipelines', value: 79, fullMark: 100 },
  { dimension: 'ML Infrastructure', value: 81, fullMark: 100 },
  { dimension: 'Research & Innovation', value: 84, fullMark: 100 },
  { dimension: 'Model Evaluation', value: 80, fullMark: 100 },
  { dimension: 'Code Quality', value: 68, fullMark: 100 },
];

const aiActivityData = [
  { week: 'W1', tasks: 47, responses: 234, reports: 12, automations: 89 },
  { week: 'W2', tasks: 52, responses: 287, reports: 15, automations: 103 },
  { week: 'W3', tasks: 61, responses: 312, reports: 18, automations: 127 },
  { week: 'W4', tasks: 58, responses: 298, reports: 14, automations: 115 },
];

const impactBreakdown = [
  { name: 'Engineering', value: 35, color: '#6366f1' },
  { name: 'Documentation', value: 25, color: '#8b5cf6' },
  { name: 'Communication', value: 22, color: '#a855f7' },
  { name: 'Planning', value: 18, color: '#d946ef' },
];

const aiImpactBreakdown = [
  { name: 'Customer Support', value: 40, color: '#10b981' },
  { name: 'Data Processing', value: 28, color: '#34d399' },
  { name: 'Report Generation', value: 20, color: '#6ee7b7' },
  { name: 'Scheduling', value: 12, color: '#a7f3d0' },
];

// Projects breakdown data
const projectsBreakdown = [
  {
    name: 'Project Phoenix',
    repo: 'acme/project-phoenix',
    prsAuthored: 174,
    prsReviewed: 134,
    effort: 0.35,
    impact: 'high',
    timeline: 'Sep 2024 - Jan 2025',
    status: 'active',
    highlights: [
      'Export feature with native tables',
      'Automated error fixing with AI SDK',
      'JIRA integration with Kanban view',
      'Validator integration'
    ]
  },
  {
    name: 'Project Titan',
    repo: 'acme/project-titan',
    prsAuthored: 287,
    prsReviewed: 447,
    effort: 0.40,
    impact: 'high',
    timeline: 'Jun - Oct 2025',
    status: 'active',
    highlights: [
      'Chatbot functionality',
      'Feature integration (164K lines)',
      'Production transcript reviews',
      'URL expander functionality'
    ]
  },
  {
    name: 'Voice Assistant',
    repo: 'acme/voice-assistant',
    prsAuthored: 181,
    prsReviewed: null,
    effort: 0.15,
    impact: 'high',
    timeline: 'Sep 2024 - Feb 2025',
    status: 'completed',
    highlights: [
      'LLM integration',
      'Bulk data fetching',
      'Model fine-tuning',
      'Custom ruleset'
    ]
  },
  {
    name: 'Project Atlas',
    repo: null,
    prsAuthored: null,
    prsReviewed: null,
    effort: 0.10,
    impact: 'medium',
    timeline: '2025 - Present',
    status: 'active',
    highlights: [
      'Conversational LLM search system',
      'Model fine-tuning partnership',
      'External model evaluation',
      'User research & testing'
    ]
  }
];

// Business impact data
const businessImpact = {
  featuresShipped: {
    current: 12,
    previous: 8,
    trend: '+50%',
    breakdown: [
      { name: 'User Authentication', status: 'launched', impact: 'high' },
      { name: 'Payment Flow Redesign', status: 'launched', impact: 'high' },
      { name: 'Dashboard V2', status: 'launched', impact: 'medium' },
      { name: 'API Rate Limiting', status: 'launched', impact: 'low' },
      { name: 'Real-time Notifications', status: 'launched', impact: 'high' },
      { name: 'Search Optimization', status: 'launched', impact: 'medium' },
      { name: 'Mobile App Refactor', status: 'launched', impact: 'high' },
      { name: 'Analytics Dashboard', status: 'launched', impact: 'medium' },
      { name: 'User Settings', status: 'launched', impact: 'low' },
      { name: 'Admin Panel', status: 'launched', impact: 'medium' },
      { name: 'Data Export', status: 'launched', impact: 'low' },
      { name: 'Multi-factor Auth', status: 'launched', impact: 'high' }
    ]
  },

  incidentsResolved: {
    current: 7,
    previous: 5,
    trend: '+40%',
    avgMTTR: { current: 2.3, previous: 3.1, unit: 'hrs', trend: '-26%' },
    breakdown: [
      {
        name: 'API Timeout Storm',
        severity: 'critical',
        mttr: 1.5,
        preventedDowntime: '45min',
        evidence: [
          { type: 'pagerduty', incidentId: 'PD-1234' },
          { type: 'github', pr: 'doordash/repo#892' }
        ]
      },
      {
        name: 'Database Connection Pool Exhaustion',
        severity: 'critical',
        mttr: 2.1,
        preventedDowntime: '2hr',
        evidence: [
          { type: 'pagerduty', incidentId: 'PD-1235' },
          { type: 'github', pr: 'doordash/repo#895' }
        ]
      },
      {
        name: 'Cache Invalidation Bug',
        severity: 'high',
        mttr: 3.2,
        preventedDowntime: '1hr',
        evidence: [
          { type: 'pagerduty', incidentId: 'PD-1236' },
          { type: 'notion', doc: 'Postmortem: Cache Invalidaton' }
        ]
      },
      {
        name: 'Memory Leak in Worker Service',
        severity: 'high',
        mttr: 2.8,
        preventedDowntime: '30min',
        evidence: [
          { type: 'pagerduty', incidentId: 'PD-1237' },
          { type: 'github', pr: 'doordash/repo#901' }
        ]
      },
      {
        name: 'Race Condition in Payment Flow',
        severity: 'medium',
        mttr: 4.5,
        preventedDowntime: '15min',
        evidence: [
          { type: 'pagerduty', incidentId: 'PD-1238' },
          { type: 'github', pr: 'doordash/repo#903' }
        ]
      },
      {
        name: 'Slow Query Performance Degradation',
        severity: 'medium',
        mttr: 1.2,
        preventedDowntime: '20min',
        evidence: [
          { type: 'pagerduty', incidentId: 'PD-1239' },
          { type: 'github', pr: 'doordash/repo#907' }
        ]
      },
      {
        name: 'Config Drift in Staging',
        severity: 'low',
        mttr: 0.75,
        preventedDowntime: '5min',
        evidence: [
          { type: 'pagerduty', incidentId: 'PD-1240' },
          { type: 'notion', doc: 'Staging Config Incident' }
        ]
      }
    ]
  },

  downtimePrevented: {
    current: 4,
    breakdown: [
      {
        name: 'Database Connection Pool Exhaustion',
        caughtIn: 'staging',
        severity: 'critical',
        wouldHaveCaused: '2hr outage',
        evidence: [
          { type: 'github', pr: 'doordash/repo#901' },
          { type: 'notion', doc: 'Postmortem: Connection Pool Fix' }
        ]
      },
      {
        name: 'Memory Leak in Async Worker',
        caughtIn: 'code-review',
        severity: 'critical',
        wouldHaveCaused: '4hr outage',
        evidence: [
          { type: 'github', pr: 'doordash/repo#902' }
        ]
      },
      {
        name: 'Race Condition in Payment Service',
        caughtIn: 'staging',
        severity: 'critical',
        wouldHaveCaused: '1hr outage',
        evidence: [
          { type: 'github', pr: 'doordash/repo#903' },
          { type: 'notion', doc: 'Payment Race Condition Fix' }
        ]
      },
      {
        name: 'Config Drift in Production Monitoring',
        caughtIn: 'prod',
        severity: 'high',
        wouldHaveCaused: '30min outage',
        evidence: [
          { type: 'github', pr: 'doordash/repo#904' },
          { type: 'datadog', dashboard: 'Config Monitoring Alert' }
        ]
      }
    ]
  },

  performanceImprovements: {
    current: 5,
    breakdown: [
      {
        name: 'API Latency Optimization',
        metric: 'p95 latency',
        before: '840ms',
        after: '120ms',
        improvement: '85% reduction',
        service: 'checkout-service',
        evidence: [
          { type: 'datadog', dashboard: 'Checkout API Performance' },
          { type: 'github', pr: 'doordash/repo#887' }
        ]
      },
      {
        name: 'Database Query Optimization',
        metric: 'avg query time',
        before: '450ms',
        after: '80ms',
        improvement: '82% reduction',
        service: 'order-service',
        evidence: [
          { type: 'datadog', dashboard: 'Order DB Performance' },
          { type: 'github', pr: 'doordash/repo#888' }
        ]
      },
      {
        name: 'Throughput Scaling',
        metric: 'requests/sec',
        before: '1200',
        after: '8500',
        improvement: '608% increase',
        service: 'api-gateway',
        evidence: [
          { type: 'datadog', dashboard: 'API Gateway Load Test' },
          { type: 'github', pr: 'doordash/repo#889' }
        ]
      },
      {
        name: 'Cache Hit Rate Improvement',
        metric: 'cache hit rate',
        before: '42%',
        after: '89%',
        improvement: '112% increase',
        service: 'user-service',
        evidence: [
          { type: 'datadog', dashboard: 'User Cache Performance' },
          { type: 'github', pr: 'doordash/repo#890' }
        ]
      },
      {
        name: 'Cold Start Reduction',
        metric: 'cold start time',
        before: '3.2s',
        after: '0.8s',
        improvement: '75% reduction',
        service: 'lambda-functions',
        evidence: [
          { type: 'datadog', dashboard: 'Lambda Cold Starts' },
          { type: 'github', pr: 'doordash/repo#891' }
        ]
      }
    ]
  }
};

// Mentorship data
const mentorshipData = {
  mentees: [
    {
      name: 'Alex Johnson',
      prsReviewed: 100,
      growthArea: 'Graph-based workflow implementations',
      achievements: [
        'Led chatbot feature development',
        'Built automation tools',
        'Promoted to technical lead'
      ],
      timeline: '6 months mentorship'
    },
    {
      name: 'Taylor Smith',
      prsReviewed: 'extensive collaboration',
      growthArea: 'Full-stack feature development',
      achievements: [
        'Smart automation feature',
        'Dashboard development',
        'Model filters implementation'
      ],
      timeline: '4 months collaboration'
    },
    {
      name: 'Jordan Lee',
      prsReviewed: 'regular collaboration',
      growthArea: 'System design & architecture',
      achievements: [
        'Authored 30+ page system design',
        'Staff Engineer Journey program',
        'Created operational runbooks'
      ],
      timeline: '6 months growth'
    }
  ],
  totalReviewsProvided: 583,
  totalReviewsReceived: 461,
  reviewRatio: 1.26
};

const integrations = [
  { name: 'Slack', icon: '💬', connected: true },
  { name: 'GitHub', icon: '🐙', connected: true },
  { name: 'Notion', icon: '📝', connected: true },
  { name: 'Linear', icon: '🎯', connected: true },
  { name: 'Figma', icon: '🎨', connected: false },
  { name: 'Salesforce', icon: '☁️', connected: true },
  { name: 'Jira', icon: '📋', connected: false },
  { name: 'Google Workspace', icon: '📧', connected: true },
];

// Evidence data for each radar dimension
const dimensionEvidence = {
  'Project Impact': [
    {
      type: 'slack',
      author: 'Taylor Brown',
      message: 'Huge shoutout to @alex for leading the auth refactor - login failures down 40%! 🎉',
      channel: '#engineering',
      timestamp: '2 days ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'Jordan Lee',
      message: 'The auth module is so much cleaner now. Alex\'s refactor made our lives 10x easier.',
      channel: '#backend',
      timestamp: '5 days ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Q4 Impact Report - Auth Module Refactor',
      preview: 'Reduced login failures by 40%, improved security posture, 99.9% uptime achieved...',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'github',
      title: 'PR #847: Authentication module complete refactor',
      status: 'Merged',
      additions: 2341,
      deletions: 892,
      timestamp: '3 weeks ago',
      link: '#'
    }
  ],
  'Velocity': [
    {
      type: 'github',
      title: 'PR #891: API performance optimization',
      status: 'Merged',
      additions: 456,
      deletions: 123,
      timestamp: '1 day ago',
      link: '#'
    },
    {
      type: 'github',
      title: 'PR #889: Rate limiting improvements',
      status: 'Merged',
      additions: 234,
      deletions: 67,
      timestamp: '2 days ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'Chris Davis',
      message: 'Alex shipped 3 PRs today! 🚀 On fire!',
      channel: '#shoutouts',
      timestamp: '5 hours ago',
      link: '#'
    },
    {
      type: 'github',
      title: 'PR #882: Cache layer implementation',
      status: 'Merged',
      additions: 892,
      deletions: 45,
      timestamp: '4 days ago',
      link: '#'
    }
  ],
  'Code Quality': [
    {
      type: 'github',
      title: 'Review Comment on PR #902',
      author: 'Alex Morgan',
      message: 'Great catch on the race condition. I added a mutex here to prevent this.',
      timestamp: '2 days ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Code Review Guidelines - Updated by Alex',
      preview: 'Added best practices for concurrent patterns, error handling, and testing...',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'Sam Rivera Rivera',
      message: 'Alex\'s code review caught a critical bug before prod. You\'re the best! 🙌',
      channel: '#engineering',
      timestamp: '6 days ago',
      link: '#'
    }
  ],
  'Collaboration': [
    {
      type: 'github',
      title: 'Review on PR #895',
      author: 'Alex Morgan',
      message: 'This looks great! One suggestion: maybe extract this into a helper function?',
      timestamp: '3 days ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Team Retrospective Notes',
      preview: 'Alex facilitated the sprint retro. Great discussion about code review process...',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'New Hire Team',
      message: 'Thanks @alex for the amazing pair programming session today! Learned so much.',
      channel: '#engineering',
      timestamp: '4 days ago',
      link: '#'
    },
    {
      type: 'github',
      title: 'PR #878: Cross-team API integration',
      status: 'Merged',
      timestamp: '2 weeks ago',
      link: '#'
    }
  ],
  'Tech Leadership': [
    {
      type: 'gdoc',
      title: 'Microservices Architecture RFC',
      preview: 'Authored by Alex. Comprehensive proposal for migrating to microservices...',
      timestamp: '3 weeks ago',
      link: '#'
    },
    {
      type: 'slack',
      channel: '#architecture',
      message: 'Alex presenting the new architecture proposal tomorrow at 2pm. All leads please attend.',
      timestamp: '2 weeks ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Technical Decision Log: Auth Service',
      preview: 'Decision: Use JWT with refresh tokens. Rationale: Better security, UX...',
      timestamp: '4 weeks ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Q4 Technical Roadmap',
      preview: 'Lead author. Prioritized initiatives for the quarter including auth refactor...',
      timestamp: '1 month ago',
      link: '#'
    }
  ],
  'Review Depth': [
    {
      type: 'github',
      title: 'Review Comment on PR #912',
      author: 'Alex Morgan',
      message: 'Found a potential SQL injection here. Please use prepared statements.',
      timestamp: '1 day ago',
      link: '#'
    },
    {
      type: 'github',
      title: 'Review on PR #908',
      author: 'Alex Morgan',
      message: 'Comprehensive review! 12 comments. Love the test coverage you added.',
      timestamp: '4 days ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'Dev Team',
      message: 'Alex\'s reviews are always so thorough. Catches issues we all miss.',
      channel: '#engineering',
      timestamp: '1 week ago',
      link: '#'
    }
  ],
  'Knowledge Share': [
    {
      type: 'gdoc',
      title: 'Microservices Architecture Guide',
      preview: 'Comprehensive guide for new engineers. Covers patterns, trade-offs, and best practices...',
      timestamp: '2 weeks ago',
      link: '#'
    },
    {
      type: 'slack',
      channel: '#tech-talks',
      message: 'Tech talk tomorrow: Alex on "Refactoring Legacy Systems". Don\'t miss it!',
      timestamp: '3 weeks ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Onboarding Checklist - Updated',
      preview: 'Alex added 5 new resources to the dev onboarding path...',
      timestamp: '3 weeks ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Design Patterns Handbook',
      preview: 'Internal guide covering 23 common patterns with examples...',
      timestamp: '1 month ago',
      link: '#'
    }
  ],
  'Incident Response': [
    {
      type: 'slack',
      channel: '#incidents',
      message: '🚨 Production incident. Alex investigating. ETA 5 min.',
      timestamp: '2 weeks ago',
      link: '#'
    },
    {
      type: 'slack',
      channel: '#incidents',
      message: '✅ Incident resolved. Alex identified the race condition in payment processing.',
      timestamp: '2 weeks ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Postmortem: Payment Gateway Outage',
      preview: 'Root cause: Race condition in payment service. Fix implemented by Alex in 23 min...',
      timestamp: '2 weeks ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'On-Runbook: Payment Service',
      preview: 'Authored comprehensive runbook. Added common issues and solutions...',
      timestamp: '1 month ago',
      link: '#'
    }
  ],
  'Business Impact': [
    {
      type: 'slack',
      author: 'Product Manager',
      message: '12 features shipped this quarter! Authentication, Payment Flow, and Dashboard V2 all launched successfully. Great work!',
      channel: '#product-launch',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'pagerduty',
      title: 'API Timeout Storm - PD-1234',
      preview: 'Critical incident resolved in 1.5h. Prevented 45min of downtime. Root cause: connection pool exhaustion...',
      timestamp: '3 weeks ago',
      link: '#'
    },
    {
      type: 'github',
      pr: 'doordash/repo#901',
      title: 'Database Connection Pool Fix',
      preview: 'Caught critical bug in staging that would have caused 2hr outage. Added monitoring and alerts...',
      timestamp: '1 month ago',
      link: '#'
    },
    {
      type: 'datadog',
      title: 'Checkout API Performance Dashboard',
      preview: 'API latency optimization complete: p95 reduced from 840ms to 120ms (85% improvement)...',
      timestamp: '2 weeks ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Postmortem: Cache Invalidation Bug',
      preview: 'Resolved in 3.2h. High severity incident. Implemented distributed cache invalidation with versioning...',
      timestamp: '1 month ago',
      link: '#'
    }
  ],
  'People Development': [
    {
      type: 'slack',
      author: 'Engineering Manager',
      message: 'The workflow implementation was mainly driven by you and Alex/Taylor have benefited tremendously from your mentorship.',
      channel: '#engineering',
      timestamp: '1 month ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Staff Engineer Journey Program - Alex Morgan',
      preview: 'Selected as Staff Guest to share expertise on AI trends, end-to-end ownership patterns, talking to customers...',
      timestamp: '6 months ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Mentee Growth Tracking',
      preview: 'Alex Johnson: Promoted to lead chatbot project. Taylor Smith: Shipped automation features. Jordan Lee: Authored 30-page system design...',
      timestamp: '3 months ago',
      link: '#'
    }
  ]
};

// Evidence data for AI agent dimensions
const aiDimensionEvidence = {
  'Accuracy': [
    {
      type: 'slack',
      channel: '#customer-support',
      message: '📊 Weekly accuracy report: Sam Rivera achieved 98.7% accuracy this week!',
      timestamp: '1 day ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'AI Accuracy Validation Report',
      preview: 'Independent audit confirms 98.7% accuracy rate across 1,247 resolved tickets...',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Customer Satisfaction Metrics',
      preview: 'CSAT score for AI-resolved tickets: 4.8/5. Only 2.3% escalation rate...',
      timestamp: '2 weeks ago',
      link: '#'
    }
  ],
  'Efficiency': [
    {
      type: 'slack',
      author: 'Support Team Lead',
      message: 'AI reduced average response time from 4.2h to 1.1h. Incredible improvement! 🚀',
      channel: '#support-metrics',
      timestamp: '3 days ago',
      link: '#'
    },
    {
      type: 'github',
      title: 'Performance Dashboard: Q4 Metrics',
      status: 'Updated',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Efficiency Analysis Report',
      preview: 'AI handles 312 hours of work per week. 73% reduction in response time...',
      timestamp: '2 weeks ago',
      link: '#'
    }
  ],
  'Response Quality': [
    {
      type: 'slack',
      author: 'Customer Review',
      message: '"The AI agent was more helpful than the human agent I spoke with last time." ⭐⭐⭐⭐⭐',
      channel: '#customer-feedback',
      timestamp: '2 days ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Response Quality Audit',
      preview: '91% of AI responses rated as "helpful" or "very helpful" by customers...',
      timestamp: '1 week ago',
      link: '#'
    }
  ],
  'Automation': [
    {
      type: 'github',
      title: 'PR #1247: Added 5 new automation workflows',
      status: 'Merged',
      additions: 567,
      deletions: 45,
      timestamp: '1 day ago',
      link: '#'
    },
    {
      type: 'slack',
      channel: '#engineering',
      message: 'Sam Rivera now automates 89 different workflows, up from 67 last month',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Automation Coverage Report',
      preview: '89% of Tier 1 support requests now fully automated...',
      timestamp: '2 weeks ago',
      link: '#'
    }
  ],
  'Cost Savings': [
    {
      type: 'gdoc',
      title: 'Q4 ROI Analysis - Sam Rivera',
      preview: 'Calculated ROI: 312%. Cost savings: $47,200. Payback period: 3 weeks...',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'CFO Office',
      message: 'The AI support agent has saved us $47,200 this quarter. Absolutely worth the investment.',
      channel: '#company-all',
      timestamp: '2 weeks ago',
      link: '#'
    }
  ],
  'Reliability': [
    {
      type: 'slack',
      channel: '#incidents',
      message: '📈 Uptime report: Sam Rivera - 99.94% uptime. Only 23 minutes downtime in Q4',
      timestamp: '3 days ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'SLA Compliance Report',
      preview: '99.8% SLA compliance. Average resolution time: 1.1 hours (target: 2 hours)...',
      timestamp: '1 week ago',
      link: '#'
    }
  ],
  'Learning': [
    {
      type: 'github',
      title: 'Training Run #47: Model fine-tuning',
      status: 'Completed',
      timestamp: '2 days ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Model Improvement Log',
      preview: 'Accuracy improved from 94% to 98.7% through 47 training iterations...',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'slack',
      channel: '#engineering',
      message: 'AI model now handles 15 new ticket types after latest training round',
      timestamp: '3 weeks ago',
      link: '#'
    }
  ],
  'Integration': [
    {
      type: 'slack',
      channel: '#engineering',
      message: 'Sam Rivera now integrated with Zendesk, Intercom, and Slack',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'github',
      title: 'PR #1301: Salesforce integration complete',
      status: 'Merged',
      timestamp: '2 weeks ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Integration Documentation',
      preview: 'Connected to 8 external systems. Full API documentation available...',
      timestamp: '3 weeks ago',
      link: '#'
    }
  ]
};

// Evidence data for AI Engineer dimensions
const aiEngineerDimensionEvidence = {
  'Model Architecture': [
    {
      type: 'github',
      title: 'PR #1523: Transformer architecture optimization',
      status: 'Merged',
      additions: 1234,
      deletions: 456,
      timestamp: '3 days ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Multi-modal Architecture Design',
      preview: 'Designed scalable architecture for text + image models. Improved inference latency by 40%...',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'ML Team Lead',
      message: 'The new architecture design is brilliant. 3x faster training with same accuracy.',
      channel: '#ml-team',
      timestamp: '2 weeks ago',
      link: '#'
    }
  ],
  'Training & Tuning': [
    {
      type: 'github',
      title: 'Training Run #89: Fine-tuning LLaMA-70B',
      status: 'Completed',
      timestamp: '1 day ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Model Training Experiments Log',
      preview: 'Achieved 92% accuracy on benchmark dataset with 50% less training data...',
      timestamp: '3 days ago',
      link: '#'
    },
    {
      type: 'slack',
      channel: '#ml-experiments',
      message: 'New fine-tuning approach reduced training time from 48h to 12h. 🚀',
      timestamp: '1 week ago',
      link: '#'
    }
  ],
  'Prompt Engineering': [
    {
      type: 'gdoc',
      title: 'Prompt Library v2.0',
      preview: 'Curated 200+ production prompts with 47% better response quality...',
      timestamp: '4 days ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'Product Manager',
      message: 'The prompt templates you created reduced hallucinations by 60%. Amazing work!',
      channel: '#product',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'github',
      title: 'PR #1498: Automated prompt evaluation',
      status: 'Merged',
      timestamp: '2 weeks ago',
      link: '#'
    }
  ],
  'Data Pipelines': [
    {
      type: 'github',
      title: 'PR #1412: Distributed data processing pipeline',
      status: 'Merged',
      additions: 892,
      deletions: 234,
      timestamp: '5 days ago',
      link: '#'
    },
    {
      type: 'notion',
      title: 'Data Infrastructure Documentation',
      preview: 'Built scalable pipeline processing 10TB daily. 99.9% uptime achieved...',
      timestamp: '2 weeks ago',
      link: '#'
    }
  ],
  'ML Infrastructure': [
    {
      type: 'github',
      title: 'PR #1389: GPU cluster optimization',
      status: 'Merged',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'ML Infrastructure Cost Analysis',
      preview: 'Reduced GPU costs by 35% through spot instance optimization and better scheduling...',
      timestamp: '3 weeks ago',
      link: '#'
    },
    {
      type: 'slack',
      channel: '#infrastructure',
      message: 'Model deployment now takes 5 minutes instead of 2 hours. Incredible improvement!',
      timestamp: '1 month ago',
      link: '#'
    }
  ],
  'Research & Innovation': [
    {
      type: 'notion',
      title: 'Research Paper Summaries - Q4',
      preview: 'Reviewed and summarized 47 papers. Identified 5 techniques for production use...',
      timestamp: '2 weeks ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Innovation Proposal: Novel Attention Mechanism',
      preview: 'Proposed and prototyped new attention pattern. 15% performance improvement in tests...',
      timestamp: '1 month ago',
      link: '#'
    },
    {
      type: 'slack',
      channel: '#ml-research',
      message: 'Presented at AI Research meetup. Great feedback on the new approach!',
      timestamp: '6 weeks ago',
      link: '#'
    }
  ],
  'Model Evaluation': [
    {
      type: 'github',
      title: 'Evaluation Framework v3.0',
      status: 'Released',
      timestamp: '1 week ago',
      link: '#'
    },
    {
      type: 'gdoc',
      title: 'Model Performance Benchmarking',
      preview: 'Comprehensive evaluation across 12 benchmarks. Top 3 in 7 categories...',
      timestamp: '2 weeks ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'Engineering Manager',
      message: 'Your evaluation framework caught a critical regression before production. Lifesaver!',
      channel: '#engineering',
      timestamp: '3 weeks ago',
      link: '#'
    }
  ],
  'Code Quality': [
    {
      type: 'github',
      title: 'Review Comment on PR #1567',
      author: 'Alex Morgan',
      message: 'Good approach! Consider adding type hints here for better maintainability.',
      timestamp: '2 days ago',
      link: '#'
    },
    {
      type: 'slack',
      author: 'Tech Lead',
      message: 'Thanks for the thorough code reviews. Your ML expertise is invaluable.',
      channel: '#engineering',
      timestamp: '1 week ago',
      link: '#'
    }
  ]
};

// Evidence card component for displaying integration data
function EvidenceCard({ evidence }) {
  const getIcon = () => {
    switch (evidence.type) {
      case 'slack':
        return <MessageSquare className="w-4 h-4" />;
      case 'gdoc':
        return <FileText className="w-4 h-4" />;
      case 'github':
        return <GitPullRequest className="w-4 h-4" />;
      case 'notion':
        return <FileCode className="w-4 h-4" />;
      case 'pagerduty':
        return <AlertCircle className="w-4 h-4" />;
      case 'datadog':
        return <Activity className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getColor = () => {
    switch (evidence.type) {
      case 'slack':
        return 'bg-purple-500/20 text-purple-400';
      case 'gdoc':
        return 'bg-blue-500/20 text-blue-400';
      case 'github':
        return evidence.status === 'Merged' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400';
      case 'notion':
        return 'bg-pink-500/20 text-pink-400';
      case 'pagerduty':
        return 'bg-orange-500/20 text-orange-400';
      case 'datadog':
        return 'bg-cyan-500/20 text-cyan-400';
      default:
        return 'bg-slate-500/20 text-slate-400';
    }
  };

  const renderContent = () => {
    switch (evidence.type) {
      case 'slack':
        return (
          <>
            {evidence.author && <div className="text-xs text-slate-500 mb-1">{evidence.author}</div>}
            <p className="text-sm text-slate-200 mb-1">{evidence.message}</p>
            {evidence.channel && <div className="text-xs text-purple-400">{evidence.channel}</div>}
          </>
        );
      case 'gdoc':
        return (
          <>
            <div className="text-sm font-medium text-slate-200 mb-1">{evidence.title}</div>
            <p className="text-xs text-slate-400">{evidence.preview}</p>
          </>
        );
      case 'github':
        return (
          <>
            <div className="text-sm font-medium text-slate-200 mb-1">{evidence.title}</div>
            <div className="flex items-center gap-2 text-xs">
              {evidence.status && <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">{evidence.status}</span>}
              {evidence.additions && <span className="text-emerald-400">+{evidence.additions}</span>}
              {evidence.deletions && <span className="text-red-400">-{evidence.deletions}</span>}
            </div>
          </>
        );
      case 'notion':
        return (
          <>
            <div className="text-sm font-medium text-slate-200 mb-1">{evidence.title}</div>
            <p className="text-xs text-slate-400">{evidence.preview}</p>
          </>
        );
      case 'pagerduty':
        return (
          <>
            <div className="text-sm font-medium text-slate-200 mb-1">{evidence.title}</div>
            <p className="text-xs text-slate-400">{evidence.preview}</p>
          </>
        );
      case 'datadog':
        return (
          <>
            <div className="text-sm font-medium text-slate-200 mb-1">{evidence.title}</div>
            <p className="text-xs text-slate-400">{evidence.preview}</p>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <a
      href={evidence.link}
      className="block bg-slate-700/50 rounded-lg p-3 hover:bg-slate-700 transition-colors cursor-pointer group"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getColor()}`}>
          {getIcon()}
        </div>
        {/* Content */}
        <div className="flex-1 min-w-0">
          {renderContent()}
          <div className="text-xs text-slate-500 mt-1">{evidence.timestamp}</div>
        </div>
        {/* External Link */}
        <ExternalLink className="w-4 h-4 text-slate-500 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </a>
  );
}

// Landing Page Component - Static marketing page
function LandingPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <Link to="/" className="text-xl font-semibold tracking-tight">Ledger</Link>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#demo" className="hover:text-white transition-colors">Demo</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#docs" className="hover:text-white transition-colors">Docs</a>
            <Link to="/otel" className="hover:text-cyan-400 transition-colors flex items-center gap-1">
              📊 AI Metrics
            </Link>
            <Link to="/benchmark" className="hover:text-orange-400 transition-colors flex items-center gap-1">
              ⚡ Benchmark
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/benchmark" className="px-4 py-2 bg-orange-600 hover:bg-orange-500 rounded-lg text-sm font-medium transition-colors">
              ⚡ Benchmark
            </Link>
            <Link to="/otel" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm font-medium transition-colors">
              📊 AI Telemetry
            </Link>
            <Link to="/dashboard" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors">
              See Dashboard
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-sm mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Unified performance tracking for every contributor</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
              Performance tracking for{' '}
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                every worker
              </span>
              <br />—human or AI.
            </h1>

            <p className="text-xl text-slate-400 max-w-2xl mb-10 leading-relaxed">
              Automatically aggregate contributions across your entire stack.
              Generate impact summaries for review cycles. Measure ROI on humans, AI tools, and agents.
              One unified system for accountability.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/dashboard"
                className="group px-6 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-all flex items-center gap-2"
              >
                Explore Interactive Demo
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button className="px-6 py-3 border border-slate-700 hover:border-slate-600 rounded-xl font-medium transition-colors">
                Watch 2-min Overview
              </button>
            </div>
          </div>

          {/* Hero Visual - Mini Dashboard Preview */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent z-10 pointer-events-none" />
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 backdrop-blur">
              <div className="flex items-center gap-4 mb-6">
                <div className="flex gap-2">
                  <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white">
                    <User className="w-4 h-4" />
                    Alex Morgan
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-400">
                    <User className="w-4 h-4" />
                    Sam Rivera
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-400">
                    <Brain className="w-4 h-4" />
                    Jordan
                  </button>
                </div>
                <div className="flex-1" />
                <span className="text-xs text-slate-500">Q4 2024 Review Cycle</span>
              </div>

              {/* Static Preview */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-4">
                <div className="text-sm text-slate-400 mb-2">Engineering Profile</div>
                <div className="h-32 flex items-center justify-center text-slate-500 text-sm">
                  Interactive dashboard preview
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Performance reviews are broken.
              <br />
              <span className="text-slate-400">AI impact is impossible to measure.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Manual aggregation is painful</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Employees spend 5-10 hours each review cycle digging through Slack, GitHub,
                Notion, and email to justify their impact. Most contributions get missed.
              </p>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4">
                <Bot className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">AI impact is invisible</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Companies spend millions on AI tools and agents but can't measure their actual impact.
                Are they helping or hurting productivity? There's no way to know.
              </p>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Humans and AI can't be compared</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                As hybrid teams emerge, there's no unified framework to understand contribution
                across human employees and AI workers doing overlapping tasks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                One system for{' '}
                <span className="text-indigo-400">every contributor</span>
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Ledger connects to your entire work stack and automatically builds
                a comprehensive activity record for each contributor—whether they're
                a senior engineer or an AI support agent.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Link2, text: 'Connect once, track everything automatically' },
                  { icon: Brain, text: 'AI-generated impact summaries for review cycles' },
                  { icon: TrendingUp, text: 'ROI tracking for humans and AI in real-time' },
                  { icon: Shield, text: 'SOC 2 compliant, enterprise-ready security' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-indigo-400" />
                    </div>
                    <span className="text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-800/30 border border-slate-700 rounded-2xl p-6">
              <div className="text-sm text-slate-400 mb-4">Connected Integrations</div>
              <div className="grid grid-cols-4 gap-3">
                {integrations.map((int, i) => (
                  <div key={i} className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-center">
                    <span className="text-xs font-medium">{int.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to see the full picture?
          </h2>
          <p className="text-xl text-slate-400 mb-10">
            Start tracking performance across your entire team—humans and AI alike.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/dashboard"
              className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl font-semibold transition-colors"
            >
              Try the Demo
            </Link>
            <button className="px-8 py-3 border border-slate-700 hover:border-slate-600 rounded-xl font-medium transition-colors">
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Layers className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium">Ledger</span>
            </div>
            <div className="text-sm text-slate-500">
              © 2024 Ledger. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Dashboard Component - Full page dashboard (was modal)
function Dashboard() {
  const [activeView, setActiveView] = useState('human');
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [selectedToolHeatmap, setSelectedToolHeatmap] = useState(null);

  const humanMetrics = {
    prsAuthored: { current: 47, previous: 42, trend: '+12%' },
    prsReviewed: { current: 89, previous: 72, trend: '+24%' },
    projectsShipped: { current: 3, previous: 2, trend: '+50%' },
    linesAdded: 12847,
    linesRemoved: 3421,
    avgReviewTime: { current: 4.2, previous: 5.1, trend: '-18%', unit: 'hrs' },
    reviewCoverage: { current: 94, previous: 87, trend: '+8%' },
    incidentsResolved: 7,
    designDocs: 5,
    impactScore: 92,
    keyHighlights: [
      'Led authentication module refactor (40% reduction in login failures)',
      'Resolved 23 customer-reported issues',
      'Mentored 2 junior developers',
      'Authored 5 technical design docs',
      'Resolved payment gateway outage in 23 minutes'
    ]
  };

  const aiMetrics = {
    totalContributions: 2847,
    roi: '$47,200',
    costSaved: '312 hrs',
    accuracy: '98.7%',
    topArea: 'Customer Support',
    keyHighlights: [
      'Resolved 1,247 support tickets autonomously',
      'Generated 47 weekly reports',
      'Processed 15,000 data entries',
      'Reduced response time by 73%'
    ]
  };

  const aiEngineerMetrics = {
    modelsTrained: { current: 12, previous: 8, trend: '+50%' },
    experimentsRun: { current: 47, previous: 35, trend: '+34%' },
    papersReviewed: { current: 23, previous: 18, trend: '+28%' },
    inferenceCalls: 4580000,
    dataProcessed: '8.2TB',
    avgTrainTime: { current: 4.8, previous: 12.4, trend: '-61%', unit: 'hrs' },
    gpuOptimization: { current: 35, previous: 0, trend: '+35%' },
    productionDeployments: 6,
    patentsFiled: 2,
    impactScore: 89,
    keyHighlights: [
      'Reduced model training time by 61%',
      'Built evaluation framework used across 5 teams',
      'Architected multi-modal model serving 10M daily inferences',
      'Published 2 internal research papers',
      'Optimized GPU costs saving $125K annually'
    ]
  };

  const juniorMetrics = {
    prsAuthored: { current: 18, previous: 12, trend: '+50%' },
    prsReviewed: { current: 8, previous: 5, trend: '+60%' },
    projectsShipped: { current: 1, previous: 0, trend: '+1' },
    linesAdded: 3842,
    linesRemoved: 1241,
    avgReviewTime: { current: 6.8, previous: 8.2, trend: '-17%', unit: 'hrs' },
    reviewCoverage: { current: 45, previous: 38, trend: '+18%' },
    incidentsResolved: 2,
    designDocs: 1,
    impactScore: 61,
    keyHighlights: [
      'Completed first solo feature implementation',
      'Participated in 3 code reviews',
      'Attended daily standups and sprint planning',
      'Fixed 5 bugs in production',
      'Learning from senior engineers through pair programming'
    ]
  };

  // AI tooling assistance data for each profile
  const aiToolingData = {
    human: {
      assistanceLevel: 42, // % of work AI-assisted
      tools: [
        { name: 'GitHub Copilot', usage: 'Daily', category: 'Coding' },
        { name: 'Cursor', usage: 'Weekly', category: 'Refactoring' },
        { name: 'Claude', usage: 'Weekly', category: 'Documentation' },
        { name: 'ChatGPT', usage: 'Weekly', category: 'Research' }
      ],
      breakdown: {
        coding: 38,
        debugging: 35,
        testing: 28,
        documentation: 55,
        codeReview: 22
      },
      trend: '+12% from last quarter'
    },
    agent: {
      assistanceLevel: 28, // Junior engineer uses less AI
      tools: [
        { name: 'GitHub Copilot', usage: 'Weekly', category: 'Coding' },
        { name: 'ChatGPT', usage: 'Weekly', category: 'Debugging' }
      ],
      breakdown: {
        coding: 25,
        debugging: 30,
        testing: 18,
        documentation: 42,
        codeReview: 15
      },
      trend: '+8% from last quarter'
    },
    ai: {
      assistanceLevel: 87, // % of work AI-assisted
      tools: [
        { name: 'Claude', usage: 'Daily', category: 'Architecture' },
        { name: 'Cursor', usage: 'Daily', category: 'Coding' },
        { name: 'GitHub Copilot', usage: 'Daily', category: 'Implementation' },
        { name: 'Aider', usage: 'Weekly', category: 'Refactoring' },
        { name: 'Replit Agent', usage: 'Weekly', category: 'Prototyping' },
        { name: 'v0.dev', usage: 'Weekly', category: 'UI Generation' }
      ],
      breakdown: {
        coding: 92,
        debugging: 78,
        testing: 85,
        documentation: 88,
        codeReview: 75
      },
      trend: '+23% from last quarter'
    }
  };

  // AI Tool heatmap data - 4 weeks of daily usage (28 days)
  const toolHeatmapData = {
    human: {
      'GitHub Copilot': [12, 15, 8, 14, 18, 22, 19, 11, 16, 20, 17, 13, 9, 14, 21, 18, 15, 12, 19, 23, 17, 14, 11, 16, 20, 18, 15, 13],
      'Cursor': [0, 0, 5, 0, 0, 8, 6, 0, 0, 4, 7, 0, 0, 3, 9, 5, 0, 0, 6, 8, 4, 0, 0, 5, 7, 6, 0, 0],
      'Claude': [0, 3, 0, 0, 5, 0, 0, 4, 0, 0, 6, 0, 3, 0, 0, 7, 0, 0, 5, 0, 0, 8, 4, 0, 0, 6, 0, 0, 5],
      'ChatGPT': [2, 4, 3, 5, 0, 0, 4, 3, 6, 0, 0, 4, 5, 3, 0, 0, 5, 4, 0, 0, 6, 3, 4, 0, 0, 5, 4, 3, 5]
    },
    agent: {
      'GitHub Copilot': [5, 6, 4, 7, 8, 9, 7, 5, 6, 8, 7, 6, 4, 5, 9, 8, 6, 5, 7, 9, 6, 5, 4, 7, 8, 6, 5, 4],
      'ChatGPT': [3, 4, 2, 5, 0, 0, 3, 4, 5, 0, 0, 3, 4, 2, 0, 0, 5, 3, 0, 0, 6, 4, 3, 0, 0, 4, 3, 2, 4]
    },
    ai: {
      'Claude': [25, 28, 22, 30, 35, 40, 38, 28, 32, 36, 34, 29, 24, 30, 38, 35, 30, 26, 34, 39, 33, 28, 25, 31, 37, 35, 29, 27],
      'Cursor': [20, 23, 18, 25, 30, 35, 32, 22, 26, 30, 28, 24, 19, 24, 32, 29, 25, 21, 28, 33, 27, 23, 20, 26, 31, 29, 24, 22],
      'GitHub Copilot': [18, 21, 16, 23, 28, 32, 30, 20, 24, 28, 26, 22, 17, 22, 30, 27, 23, 19, 26, 31, 25, 21, 18, 23, 29, 27, 22, 20],
      'Aider': [0, 0, 6, 0, 0, 8, 7, 0, 0, 5, 9, 0, 0, 4, 10, 8, 0, 0, 7, 9, 5, 0, 0, 6, 8, 7, 0, 0],
      'Replit Agent': [0, 4, 0, 0, 6, 0, 0, 5, 0, 0, 7, 0, 4, 0, 0, 8, 0, 0, 6, 0, 0, 9, 5, 0, 0, 7, 0, 0, 6],
      'v0.dev': [0, 0, 3, 0, 0, 5, 4, 0, 0, 3, 6, 0, 0, 2, 7, 5, 0, 0, 4, 6, 3, 0, 0, 5, 4, 0, 0, 3]
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold tracking-tight">Ledger</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#demo" className="hover:text-white transition-colors">Demo</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#docs" className="hover:text-white transition-colors">Docs</a>
          </div>
          <button
            onClick={() => setShowDemo(true)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors"
          >
            See Dashboard
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-sm mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Unified performance tracking for every contributor</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
              Performance tracking for{' '}
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                every worker
              </span>
              <br />—human or AI.
            </h1>

            <p className="text-xl text-slate-400 max-w-2xl mb-10 leading-relaxed">
              Automatically aggregate contributions across your entire stack.
              Generate impact summaries for review cycles. Measure ROI on humans, AI tools, and agents.
              One unified system for accountability.
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setShowDemo(true)}
                className="group px-6 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-all flex items-center gap-2"
              >
                Explore Interactive Demo
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="px-6 py-3 border border-slate-700 hover:border-slate-600 rounded-xl font-medium transition-colors">
                Watch 2-min Overview
              </button>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent z-10 pointer-events-none" />
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 backdrop-blur">
              <div className="flex items-center gap-4 mb-6">
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveView('human')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeView === 'human'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <User className="w-4 h-4" />
                    Alex Morgan
                  </button>
                  <button
                    onClick={() => setActiveView('agent')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeView === 'agent'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <User className="w-4 h-4" />
                    Sam Rivera
                  </button>
                  <button
                    onClick={() => setActiveView('ai')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeView === 'ai'
                        ? 'bg-amber-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <Brain className="w-4 h-4" />
                    Jordan
                  </button>
                </div>
                <div className="flex-1" />
                <span className="text-xs text-slate-500">Q4 2024 Review Cycle</span>
              </div>

              {/* Mini Dashboard Preview */}
              <div className="grid grid-cols-4 gap-4">
                {activeView === 'human' ? (
                  <>
                    <div className="col-span-2 bg-slate-800/50 rounded-xl p-4">
                      <div className="text-sm text-slate-400 mb-2">Engineering Profile</div>
                      <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={engineeringRadarCurrent}>
                            <PolarGrid stroke="#334155" />
                            <PolarAngleAxis
                              dataKey="dimension"
                              tick={{ fill: '#94a3b8', fontSize: 9 }}
                            />
                            <PolarRadiusAxis
                              angle={90}
                              domain={[0, 100]}
                              tick={{ fill: '#64748b', fontSize: 8 }}
                              tickCount={5}
                            />
                            <Radar
                              dataKey="value"
                              stroke="#6366f1"
                              fill="#6366f1"
                              fillOpacity={0.3}
                              strokeWidth={2}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold">{humanMetrics.prsAuthored.current}</div>
                        <div className="text-xs text-slate-400">PRs Authored</div>
                        <div className="text-xs text-emerald-400">{humanMetrics.prsAuthored.trend}</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-purple-400">{humanMetrics.prsReviewed.current}</div>
                        <div className="text-xs text-slate-400">PRs Reviewed</div>
                        <div className="text-xs text-emerald-400">{humanMetrics.prsReviewed.trend}</div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-emerald-400">{humanMetrics.impactScore}</div>
                        <div className="text-xs text-slate-400">Impact Score</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-rose-400">{humanMetrics.projectsShipped.current}</div>
                        <div className="text-xs text-slate-400">Projects Shipped</div>
                      </div>
                    </div>
                  </>
                ) : activeView === 'agent' ? (
                  <>
                    <div className="col-span-2 bg-slate-800/50 rounded-xl p-4">
                      <div className="text-sm text-slate-400 mb-2">Engineering Profile</div>
                      <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={juniorRadarCurrent}>
                            <PolarGrid stroke="#334155" />
                            <PolarAngleAxis
                              dataKey="dimension"
                              tick={{ fill: '#94a3b8', fontSize: 9 }}
                            />
                            <PolarRadiusAxis
                              angle={90}
                              domain={[0, 100]}
                              tick={{ fill: '#64748b', fontSize: 8 }}
                              tickCount={5}
                            />
                            <Radar
                              dataKey="value"
                              stroke="#10b981"
                              fill="#10b981"
                              fillOpacity={0.3}
                              strokeWidth={2}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-emerald-400">{juniorMetrics.prsAuthored.current}</div>
                        <div className="text-xs text-slate-400">PRs Authored</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-teal-400">{juniorMetrics.projectsShipped.current}</div>
                        <div className="text-xs text-slate-400">Projects Shipped</div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold">
                          {juniorMetrics.prsReviewed.current}
                        </div>
                        <div className="text-xs text-slate-400">PRs Reviewed</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-cyan-400">{juniorMetrics.avgReviewTime.current}h</div>
                        <div className="text-xs text-slate-400">Avg Review Time</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-2 bg-slate-800/50 rounded-xl p-4">
                      <div className="text-sm text-slate-400 mb-2">AI Engineer Profile</div>
                      <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={aiEngineerRadarCurrent}>
                            <PolarGrid stroke="#334155" />
                            <PolarAngleAxis
                              dataKey="dimension"
                              tick={{ fill: '#94a3b8', fontSize: 9 }}
                            />
                            <PolarRadiusAxis
                              angle={90}
                              domain={[0, 100]}
                              tick={{ fill: '#64748b', fontSize: 8 }}
                              tickCount={5}
                            />
                            <Radar
                              dataKey="value"
                              stroke="#f59e0b"
                              fill="#f59e0b"
                              fillOpacity={0.3}
                              strokeWidth={2}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-amber-400">{aiEngineerMetrics.modelsTrained.current}</div>
                        <div className="text-xs text-slate-400">Models Trained</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-orange-400">{aiEngineerMetrics.experimentsRun.current}</div>
                        <div className="text-xs text-slate-400">Experiments Run</div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-yellow-400">{aiEngineerMetrics.avgTrainTime.current}h</div>
                        <div className="text-xs text-slate-400">Avg Train Time</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="text-2xl font-bold text-amber-500">{aiEngineerMetrics.gpuOptimization.current}%</div>
                        <div className="text-xs text-slate-400">GPU Optimization</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Performance reviews are broken.
              <br />
              <span className="text-slate-400">AI impact is impossible to measure.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Manual aggregation is painful</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Employees spend 5-10 hours each review cycle digging through Slack, GitHub,
                Notion, and email to justify their impact. Most contributions get missed.
              </p>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4">
                <Bot className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">AI impact is invisible</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Companies spend millions on AI tools and agents but can't measure their actual impact.
                Are they helping or hurting productivity? There's no way to know.
              </p>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Humans and AI can't be compared</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                As hybrid teams emerge, there's no unified framework to understand contribution
                across human employees and AI workers doing overlapping tasks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                One system for{' '}
                <span className="text-indigo-400">every contributor</span>
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Ledger connects to your entire work stack and automatically builds
                a comprehensive activity record for each contributor—whether they're
                a senior engineer or an AI support agent.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Link2, text: 'Connect once, track everything automatically' },
                  { icon: Brain, text: 'AI-generated impact summaries for review cycles' },
                  { icon: TrendingUp, text: 'ROI tracking for humans and AI in real-time' },
                  { icon: Shield, text: 'SOC 2 compliant, enterprise-ready security' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-indigo-400" />
                    </div>
                    <span className="text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-800/30 border border-slate-700 rounded-2xl p-6">
              <div className="text-sm text-slate-400 mb-4">Connected Integrations</div>
              <div className="grid grid-cols-4 gap-3">
                {integrations.map((int, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-xl text-center transition-all ${
                      int.connected
                        ? 'bg-slate-700/50 border border-slate-600'
                        : 'bg-slate-800/30 border border-slate-800 opacity-50'
                    }`}
                  >
                    <div className="text-2xl mb-1">{int.icon}</div>
                    <div className="text-xs text-slate-400">{int.name}</div>
                    {int.connected && (
                      <CheckCircle className="w-3 h-3 text-emerald-400 mx-auto mt-1" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Main App Component with Routing
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/otel" element={<OtelMetricsPage />} />
        <Route path="/otel/admin" element={<OtelMetricsPage mode="admin" />} />
        <Route path="/otel/personal" element={<OtelMetricsPage mode="personal" />} />
        <Route path="/ai-metrics" element={<OtelMetricsPage />} />
        <Route path="/benchmark" element={<BenchmarkPage />} />
      </Routes>
    </BrowserRouter>
  );
}
